import Typography from '@mui/material/Typography';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StatisticCard } from 'shared/components/ui/statistic-card';
import {
  FrequencyUnit,
  ScheduleEntry,
  ScheduleEntryState,
} from 'shared/types/habit-schedule';

import * as Styled from './statistics.style';

export type StatisticsProps = {
  target?: number;
  completionPercentage?: number;
  perfectSlots: number;
  slotsType?: FrequencyUnit;
  dateEntries: ScheduleEntry[];
};

export const Statistics: React.FC<StatisticsProps> = ({
  target,
  completionPercentage,
  perfectSlots,
  slotsType = FrequencyUnit.Week,
  dateEntries,
}) => {
  const { t } = useTranslation();

  const completionsCount = useMemo(
    () =>
      dateEntries
        .filter(({ state }) => state === ScheduleEntryState.Complete)
        .reduce((acc, { value }) => acc + (value ?? 0), 0),
    [dateEntries],
  );
  const skips = useMemo(
    () => dateEntries.filter(({ state }) => state === ScheduleEntryState.Skip),
    [dateEntries],
  );
  const failures = useMemo(
    () =>
      dateEntries.filter(({ state }) => state === ScheduleEntryState.Failure),
    [dateEntries],
  );

  return (
    <Styled.Container>
      <Typography variant="caption" component="span">
        {t('general.labels.task-statistics.statistics.title')}
      </Typography>

      <Styled.StatisticsContainer>
        <StatisticCard
          value={completionsCount}
          target={target || undefined}
          description={t(
            'general.labels.task-statistics.statistics.completions.label',
          )}
          isFormatted
        />

        <StatisticCard
          value={completionPercentage}
          description={t(
            'general.labels.task-statistics.statistics.completion-rate.label',
          )}
          isPercentage
        />

        <StatisticCard
          value={perfectSlots}
          description={t(
            `general.labels.task-statistics.statistics.perfect-slots.${slotsType}.label`,
          )}
          isFormatted
        />

        <StatisticCard
          value={skips.length}
          description={t(
            'general.labels.task-statistics.statistics.skips.label',
          )}
          isFormatted
        />

        <StatisticCard
          value={failures.length}
          description={t(
            'general.labels.task-statistics.statistics.failures.label',
          )}
          isFormatted
        />
      </Styled.StatisticsContainer>
    </Styled.Container>
  );
};
